import type { KeyboardEvent } from 'react';
import { flatten } from 'lodash';
import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import type { Asset } from '@common/interfaces/api/assetsApi';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import { isDefined } from '@common/utils/isDefined';
import { useAbortableFetch } from '@frontend/utils';
import type { IAsset } from '../interfaces/asset';
import { FilesUrlBase } from '../../../common/enums/filesUrlBase';
import { ServiceCode } from '../../../common/enums/serviceCode';
import { checkIsImageOrPdf } from '../../../common/utils/checkIsImageOrPdf';
import { ImageViewer } from './imageViewer/ImageViewer';
import { ViewerThumbnails } from './ViewerThumbnails';

type ViewerProps = {
    files: File[];
    group?: string;
    id?: string;
    selectedFileId?: string;
    setSelectedFileId?: (selectedFileId: string) => void;
    setSelectedFileType?: (isPdf: boolean) => void;
    setUrl?: (url: string) => void;
    showThumbnails: boolean;
    urlBase?: FilesUrlBase;
};

export type File = {
    filename: string;
    id?: string;
    isGeneratedPdf?: boolean;
    isUpload?: boolean;
    placement?: string;
    uploadId?: string;
};

export type FilesByPlacement = {
    [placement: string]: File[] | undefined;
};

export function Viewer({
    files: filesProp,
    group,
    id,
    selectedFileId,
    showThumbnails,
    setSelectedFileId,
    setSelectedFileType,
    setUrl: setUrlProp,
    urlBase,
}: ViewerProps) {
    const abortableFetch = useAbortableFetch();

    const [filesState, setFilesState] = useState<FilesByPlacement>({});
    const [isPdf, setIsPdf] = useState(false);
    const [selectedFileState, setSelectedFileState] = useState<File | null>(null);
    const [serviceCode, setServiceCode] = useState<ServiceCode>();
    const [viewerUrl, setViewerUrl] = useState('');
    const [flattenedFileList, setFlattenedFileList] = useState<File[]>([]);
    const [flattenedIndex, setFlattenedIndex] = useState<number>(0);
    const [viewer, setViewer] = useState<HTMLElement | null>(null);

    useEffect(() => {
        loadFiles();
    }, []);

    useEffect(() => {
        setFileUrl();
    }, [selectedFileState]);

    useEffect(() => {
        handleBlur();
    }, [viewer]);

    function handleBlur() {
        viewer?.focus();
    }

    if (!selectedFileState) {
        return null;
    }
    const showInGrayScale = serviceCode === ServiceCode.TOO || serviceCode === ServiceCode.TFO;

    return (
        <StyledViewerContainer
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            ref={setViewer}
            tabIndex={0}
        >
            {showThumbnails && (
                <ViewerThumbnails
                    files={filesState}
                    id={id}
                    onThumbnailClick={loadSelectedFile}
                    selectedFile={selectedFileState}
                    serviceCode={serviceCode}
                    urlBase={urlBase}
                />
            )}
            {isPdf ? (
                renderPdfViewer(viewerUrl)
            ) : (
                <ImageViewer
                    moveSelection={(direction) => moveSelection(direction)}
                    showGrayScaleControls={showInGrayScale}
                    url={viewerUrl}
                />
            )}
        </StyledViewerContainer>
    );

    function setFileUrl() {
        let newUrl = '';
        let newIsPdf = false;

        if (selectedFileState) {
            if (selectedFileState.isUpload) {
                newUrl = `/api/uploads/${selectedFileState.id}`;
            } else if (selectedFileState.isGeneratedPdf) {
                newUrl = selectedFileState.filename;
                newIsPdf = true;
            } else {
                newUrl = `/api/${urlBase}/${id}/assets/${selectedFileState.id}`;
            }
            newIsPdf = newIsPdf || selectedFileState.filename.toLowerCase().endsWith('.pdf');
        }

        if (setSelectedFileType) {
            setSelectedFileType(newIsPdf);
        }

        if (setUrlProp) {
            setUrlProp(newUrl);
        }

        setViewerUrl(newUrl);
        setIsPdf(newIsPdf);
    }

    async function loadSelectedFile(selectedFile: IAsset | ProcessActivityAsset) {
        setSelectedFileState(selectedFile);
        setFileUrl();

        if (setSelectedFileId && selectedFile.id) {
            setSelectedFileId(selectedFile.id);
        }

        if (selectedFile.id) {
            refreshFlattenedIndex(selectedFile.id, flattenedFileList);
        }
    }

    function renderPdfViewer(urlParam: string) {
        const pdfUrl = `/pdfViewer/web/viewer.html?file=${encodeURIComponent(urlParam)}`;

        return (
            <StyledPdfViewer>
                <iframe
                    src={pdfUrl}
                    title="pdf-viewer"
                />
            </StyledPdfViewer>
        );
    }

    async function loadFiles() {
        let files: (Asset | File)[] = (await loadAssets()) || [];

        await loadUploads(files);
        files = files.filter((file) => file.isGeneratedPdf || checkIsImageOrPdf(file.filename));

        const filesByPlacement = groupFilesByPlacement(files);
        setFilesState(filesByPlacement);

        const newFlattenedFileList = flatten(Object.values(filesByPlacement)).filter(isDefined);

        setFlattenedFileList(newFlattenedFileList);
        findSelectedFile(files, newFlattenedFileList);
    }

    function findSelectedFile(files: (File | Asset)[], newFlattenedFileList: File[]) {
        if (selectedFileId) {
            const selectedFile1 = files.find((file) => file.id === selectedFileId);
            if (selectedFile1) {
                setSelectedFileState(selectedFile1);
            }

            refreshFlattenedIndex(selectedFileId, newFlattenedFileList);
        }
    }

    async function handleKeyDown(event: KeyboardEvent) {
        if (event.key === 'ArrowLeft') {
            moveSelection('left');
        } else if (event.key === 'ArrowRight') {
            moveSelection('right');
        }
    }

    async function moveSelection(direction: 'left' | 'right') {
        let newFlattenedIndex = flattenedIndex;
        if (direction === 'left') {
            newFlattenedIndex -= 1;
        } else if (direction === 'right') {
            newFlattenedIndex += 1;
        }

        newFlattenedIndex = (newFlattenedIndex + flattenedFileList.length) % flattenedFileList.length;
        setFlattenedIndex(newFlattenedIndex);
        await loadSelectedFile(flattenedFileList[newFlattenedIndex]);
    }

    function refreshFlattenedIndex(fileId: string, flattenedFileListArg: File[]) {
        setFlattenedIndex(flattenedFileListArg?.map((file) => file.id).indexOf(fileId));
    }

    async function loadAssets() {
        const newFiles: File[] = [...filesProp];

        if (!(urlBase && id)) {
            return newFiles;
        }
        const newUrl = `/api/${urlBase}/${id}`;

        if (urlBase === FilesUrlBase.processActivities) {
            return await getProcessActivityAssets(newFiles, newUrl);
        }

        return await getAssets(newFiles, newUrl);
    }

    async function getProcessActivityAssets(files: File[], url: string) {
        if (files.length > 0) {
            return files;
        }
        const result = await abortableFetch.fetchJson(`${url}/assets`, 'GET');
        const items: File[] = result.json.items;
        setServiceCode(undefined);

        return items ?? [];
    }

    async function getAssets(files: File[], url: string) {
        const result = await abortableFetch.fetchJson(`${url}?showAssets=true`, 'GET');
        const item: { assets: File[]; code: ServiceCode; customControlData: { files: File[] } } = result.json;
        setServiceCode(item.code);

        let assetFiles = item.assets;

        if (Array.isArray(item.customControlData?.files)) {
            assetFiles = [...assetFiles, ...item.customControlData.files];
        }

        const assetViewerOpenedInNewTab = assetFiles.length && !files.length;

        if (assetViewerOpenedInNewTab || item.code === ServiceCode.TOO || item.code === ServiceCode.TFO) {
            return assetFiles;
        }

        return files;
    }

    async function loadUploads(files: File[]) {
        if (!group) {
            return;
        }
        const objectListUrl = `/api/uploads?group=${group}`;
        const response = await abortableFetch.fetchJson(objectListUrl, 'GET');

        for (const object of response.json) {
            files.push({
                filename: object.filename,
                id: object.id,
                isUpload: true,
                placement: object.placement,
            });
        }
    }
}

export function groupFilesByPlacement(files: (File | Asset)[]) {
    return files.reduce((previous, current) => {
        const placement = 'placement' in current && current.placement ? current.placement : 'free';

        return {
            ...previous,
            [placement]: [...(previous[placement] ?? []), current],
        };
    }, {} as FilesByPlacement);
}

const StyledViewerContainer = styled.div`
    height: 100%;
    width: 100%;
    outline: none;
    display: flex;
    flex-direction: column;
    gap: 24px;

    ${({ theme }) => theme.mediaQueries.large} {
        flex-direction: row;
    }
`;

const StyledPdfViewer = styled.div`
    height: 100%;
    width: 100%;
    overflow: hidden;

    iframe {
        width: 100%;
        height: 100%;
        border: 0;
    }
`;
